import { useCallback } from "react"
import useRetailCollections from "./useRetailCollections"
import type { RetailFilterCriteria, MinMaxPrice } from '../types'

/** Modèle retail traité avec les champs courts utilisés dans les filtres */
interface RetailCatalogModel {
   id: string
   label: string
   desig: string
   argu: string
   gamme?: string
   collec?: string
   fam?: string
   cycle?: string
   [key: string]: unknown
}

const useRetailFilters = () => {
   const { getAllRetailModelsAsArray } = useRetailCollections()

   /**
    * @param arr_source Tableau des modèles à filtrer
    * @param text Chaîne de caractère pour filtrage. Elle sera découpée en morceaux avec séparateur espace. Chaque morceau servant de critère de recherche (ReExp "contient")
    * @returns Tableau des modèles filtrés
    */
   const filterOnText = useCallback((arr_source: RetailCatalogModel[], text: string | null | undefined) => {
      const str = text?.trim().toUpperCase()

      if (!str) {
         return arr_source
      } else {
         let arr_final: RetailCatalogModel[] = []
         const parts_or = str.split(" ")  // Liste des blocs de texte à chercher (on applique un "ou" entre chaque bloc)
         for (const or_part of parts_or) {
            const parts_and = or_part.split("+")  // Liste des parties "et" dans un bloc de texte à chercher (on applique un "et" entre chaque partie)
            let arr_tempo = arr_source   // Au départ on considère que tout matche. On travaille sur les objets
            for (const and_part of parts_and) {
               arr_tempo = arr_tempo.filter( // On remplace par un sous-ensemble => "ET" (i.e. intersection)
                  (item) => {
                     const item_id = item.id.toUpperCase()
                     const item_label = item.label.toUpperCase()
                     const item_desig = item.desig.toUpperCase()
                     const item_argu = item.argu.toUpperCase()
                     return (
                        ((and_part.length < 4) && item_id.startsWith(and_part)) ||  // De 1 à 3 car => "ID débute par"
                        ((and_part.length >= 4) && (
                           item_id.includes(and_part) ||   // 4 car et + => "ID contient"
                           item_desig.includes(and_part) ||
                           item_argu.includes(and_part)
                        )) ||
                        item_label.includes(and_part)
                     )
                  }
               )
            }
            // On ajoute ("OU", i.e. union) seulement les items inexistants (=>uniques)
            for (const item of arr_tempo) {
               if (arr_final.indexOf(item) === -1) {
                  arr_final.push(item);
               }
            }
         }
         return arr_final
      }
   }, [])

   const filterOnCriteriaExceptPrice = useCallback((arr_source: RetailCatalogModel[], crits: RetailFilterCriteria) => {
      let arr = arr_source

      // Si on a sélectionné des GAMMES
      if (crits.gammes?.length) {
         arr = arr.filter((model) => {
            return crits.gammes!.includes(model.gamme ?? '')
         })
      }

      // Si on a sélectionné des COLLECTIONS
      if (crits.collections?.length) {
         arr = arr.filter((model) => {
            return crits.collections!.includes(model.collec ?? '')
         })
      }

      // Si on a sélectionné des FAMILLES
      if (crits.familles?.length) {
         arr = arr.filter((model) => {
            return crits.familles!.includes(model.fam ?? '')
         })
      }

      // Si on a sélectionné des TYPOLOGIES
      if (crits.typologies?.length) {
         arr = arr.filter((model) => {
            return crits.typologies!.includes((model as any).typo ?? '')
         })
      }

      // Si on a sélectionné des CYCLES
      if (crits.cycles?.length) {
         arr = arr.filter((model) => {
            return crits.cycles!.includes(model.cycle ?? '')
         })
      }

      // Si on a sélectionné une EXCLUSIVITÉ BOUTIQUE
      if (crits.exclusivite !== null && crits.exclusivite !== undefined && crits.exclusivite !== 'all') {
         arr = arr.filter((model) => {
            const excbou = !!(model as any).excbou
            return (crits.exclusivite === 'yes' && excbou) || (crits.exclusivite === 'no' && !excbou)
         })
      }

      return arr
   }, [])


   const filterOnPrice = useCallback((arr_source: RetailCatalogModel[] | null | undefined, crits: RetailFilterCriteria, all_minmax: Record<string, MinMaxPrice>) => {
      if (!arr_source || ((crits?.price_min === null) && (crits?.price_max === null))) {   // Si null ou undefined, on ne va pas plus loin...
         return arr_source
      }
      let arr = arr_source
      arr = arr.filter((item) => {
         const minMaxPrice = all_minmax[item.id]
         if (crits.price_min !== null) {
            if (crits.price_max !== null) {
               return ((minMaxPrice.min ?? 0) >= crits.price_min! && (minMaxPrice.max ?? 0) <= crits.price_max!)
            } else {
               return ((minMaxPrice.min ?? 0) >= crits.price_min!)
            }
         } else {
            return ((minMaxPrice.max ?? 0) <= crits.price_max!)
         }
      })
      return arr
   }, [])

   const filterOnCriteria = useCallback((crits: RetailFilterCriteria) => {
      return filterOnCriteriaExceptPrice(getAllRetailModelsAsArray(), crits)
   }, [filterOnCriteriaExceptPrice, getAllRetailModelsAsArray])

   return { filterOnText, filterOnCriteriaExceptPrice, filterOnPrice, filterOnCriteria }
}

export default useRetailFilters
export type { RetailCatalogModel }
